import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReplayIcon from '@mui/icons-material/Replay';
import { CreateDatePattern } from 'components/dialog/date_patterns/create';
import { UpdateDatePattern } from 'components/dialog/date_patterns/update';
import { DeleteDatePattern } from 'components/dialog/date_patterns/delete';
import { InfoDatePattern } from 'components/dialog/date_patterns/read';
import { ViewDaysDialog } from 'components/dialog/date_patterns/ViewDaysDialog';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface SubMenuProps {
    selected: any;
    cleanSelected: () => void;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}

const SubMenu: FC<SubMenuProps> = ({ selected, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openShow, setOpenShow] = useState(false);
    const [openViewDays, setOpenViewDays] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    const modalRoute = parseSectionSubpageActionRoute(location.pathname, 'accruals', 'date_pattern', ['add', 'update', 'detail']);
    const routeSelected = useMemo(() => {
        if (!modalRoute.id) return null;
        return rows.find((row: any) => String(row?.id) === String(modalRoute.id)) || null;
    }, [modalRoute.id, rows]);
    const selectedForDialog = routeSelected ? [routeSelected] : selected;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('accruals', 'date_pattern');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpenAdd(false);
        setOpenEdit(false);
        setOpenShow(false);
    };

    interface Option {
        name: string;
        icon: React.ReactNode;
        click: () => void;
        color: string;
        divider: boolean;
        disabled: boolean;
        selected: boolean;
        onlyOne: boolean;
        hidden: boolean;
        dialog?: React.ReactNode | null;
    }

    const options: Option[] = [
        {
            name: t('accruals.accrual_dates.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('accruals', 'date_pattern', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreateHoliday') ? false : true,
            dialog: <CreateDatePattern open={modalRoute.action === 'add' || openAdd} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenAdd(true); } }} rows={rows} reloading={reloading} />
        },
        {
            name: t('accruals.accrual_dates.context_menu.info'),
            icon: <VisibilityIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'date_pattern', 'detail', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            hidden: selected.length <= 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.HolidayById') ? true : false,
            dialog: <InfoDatePattern open={modalRoute.action === 'detail' || openShow} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenShow(true); } }} rows={rows} selectedRecord={selectedForDialog[0]} cleanSelected={cleanSelected}/>
        },
        {
            name: t('accruals.accrual_dates.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'date_pattern', 'update', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            hidden: selected.length !== 1 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.PatchHolidayById') ? true : false,
            dialog: <UpdateDatePattern open={modalRoute.action === 'update' || openEdit} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenEdit(true); } }} rows={rows} rowToEdit={selectedForDialog[0]} reloading={reloading} cleanSelected={cleanSelected} />
        },
        {
            name: 'Ver dias',
            icon: <CalendarMonthIcon />,
            click: () => {
                setOpenViewDays(true);
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length !== 1,
            selected: false,
            onlyOne: true,
            hidden: false,
            dialog: <ViewDaysDialog open={openViewDays} onClose={() => setOpenViewDays(false)} datePatternId={selected?.[0]?.id} name={selected?.[0]?.name} />
        },
        {
            name: t('accruals.accrual_dates.context_menu.delete'),
            icon: <DeleteIcon color='error' />,
            click: () => {
                setOpenDelete(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true,
            selected: true,
            onlyOne: false,
            hidden: selected.length === 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.DeleteHolidayById') ? true : false,
            dialog: <DeleteDatePattern open={openDelete} setOpen={setOpenDelete} selectedRecord={selected} reloading={reloading} />
        },
        {
            name: t('accruals.accrual_dates.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
            dialog: null
        }
    ];

    return (
        <>
            <Menu
                open={ Boolean(contextMenu) }
                onClose={() => setContextMenu(null) }
                anchorReference="anchorPosition"
                anchorPosition={ contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined }
            >
                {options.map((option) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled} >
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            {options.map((option) => (
                option.dialog && <React.Fragment key={`dialog-${option.name}`}>{option.dialog}</React.Fragment>
            ))}
        </>
    );
};

export { SubMenu };
